import Constants from '@/constants/const';
import fetch, { NewAxiosRequestConfig } from '@/utils/axios';
import { getCookie } from '@/utils/cookie';
import internalFetch from '@/utils/axios-internal';
/**
 * 获取匿名token
 */
export const generateToken = (ctx?): Promise<string> => {
  return fetch
    .get<AResponse, AResponse>(
      '/ec/customer/login/token/generate',
      {},
      { ctx, noWithInHeaders: [Constants.AUTH_TOKEN] }
    )
    .then(({ object }) => {
      return object?.token;
    })
    .catch(() => {
      return '';
    });
};

/**
 * 刷新token
 */
export const refreshToken = (ctx): Promise<string> => {
  return fetch
    .get<AResponse, AResponse>('/ec/customer/login/token/refresh', {}, { ctx })
    .then(({ object }) => {
      return object?.token;
    })
    .catch(() => {
      return '';
    });
};

/**
 * 获取微信签名
 * @param params
 * @param config
 */
export const getWechatInfo = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/wx/sign/package', params, config);
};

/**
 * 收藏和取消收藏
 * @param params
 * @param config
 */
export const setFavorite = (
  params: { type: string; target_id: number | string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/item/v5/me/favorites/operate', params, config);
};

/**
 * 获取收藏信息商品ids :
 * @param config
 */
export const getFavoriteIds = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/item/v5/me/favorites/product_ids', {}, config);
};

/**
 * 获取送货信息页所有收货地址 :
 * @param config
 */
export const getShippingBookAddressList = (orderId: string, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/so/address/order/book/${orderId}`, {}, config);
};

/**
 * 获取账户信息
 */
export const getAccountInfo = (params: any, ctx?): Promise<any> => {
  return fetch.get('/ec/customer/account/popup', params || {}, { ctx }).then(({ object }) => {
    return object;
  });
};

/**
 * 获取用户简要信息
 * @param params
 * @param config
 */
export const getSimpleAccountInfo = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/account/info`, { cache: true }, config);
};

/** CDP：再买相关（含历史订单数 order_num 等） */
export const getCdpAuthInfoBuyAgain = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  const buyAgainApi =
    process.env.NODE_ENV === 'development'
      ? '/ec/customer/test/auth/info/user-order-paid'
      : '/ec/customer/cdp/auth/info/user-order-paid';
  return internalFetch.get(buyAgainApi, {}, config);
};

/**
 * 获取Account页面信息 h5专用 区别于app的v2接口account/h5/portal
 * @param params
 * @param config
 */
export const getAccountPage = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/account/h5/portal`, {}, config);
};

export const getAccountPageBff = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/bff/common/account/portal`, params, config);
};

/**
 * Password Reset Get Verify Code
 * @param email
 * @param config
 * @returns
 */
export const sendResetPasswordVerifyCode = (email: string, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(
    `/ec/customer/password/verify_code/send`,
    {
      email
    },
    config
  );
};
/**
 * Password Reset Check Verify Code
 * @param email
 * @param config
 * @returns
 */
export const verifyResetPasswordCode = (
  email: string,
  verifyCode: string,
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post(
    `/ec/customer/password/verify_code/check`,
    {
      email,
      verify_code: verifyCode
    },
    config
  );
};

/**
 * 获取验证码
 * @param params
 * @param config
 */
export const getVerifyCode = (
  params: { phone_number: string; source?: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/phone/verify_code', params, config);
};

/**
 * 获取验证码 (邮箱code校验注册流程 新增手机页面流程)
 * @param params
 * @param config
 */
export const getVerifySignupCode = (
  params: { phone: string; key: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/phone/user_verify/v1/send', params, config);
};

/**
 * 邮箱code 校验注册流程 新增手机页面流程
 * @param params  code =>Verify Code
 * @param config
 */
export const SignupBindPhone = (
  params: { phone: string; code: string; check_type: string; key: string; email?: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/signup/phone/code/v1', params, config);
};

/**
 * 语言获取验证码
 * @param params
 * @param config
 * @returns
 */
export const getVoiceVerifyCode = (
  params: { phone_number: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/phone/verify_code/voice', params, config);
};

/**
 * Password Reset
 * @param email
 * @param verifyCode
 * @param newPassword
 * @param confirmPassword
 * @param oldPassword
 * @param config
 */
export const resetPasswordByVerifyCode = (
  email: string,
  verifyCode: string,
  newPassword: string,
  confirmPassword: string,
  oldPassword?: string,
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post(
    `/ec/customer/password/reset`,
    {
      email,
      verify_code: verifyCode,
      new_password: newPassword,
      confirm_password: confirmPassword,
      old_password: oldPassword || ''
    },
    config
  );
};

/**
 * 手机号登录
 * @param params
 * @param config
 */
export const loginWithPhone = (
  params?: {
    code: string;
    phone_number: string;
    referral_id: number;
    channelID?: string;
    additional?: string;
    page_url?: string;
  } & ObjectType,
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/phone', params, config);
};

/**
 * wechat 绑定email
 * @param params
 * @param config
 */
export const loginBindEmail = (
  params: { email: string; password: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/bind/email', params, config);
};
/**
 * wechat 绑定 手机号
 * @param params  code =>Verify Code
 * @param config
 */
export const loginBindPhone = (
  params: { phone_number: string; code: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/bind/phone', params, config);
};

/*
 * 邮箱登录
 * @param params
 * @param config
 */
export const loginWithEmail = (
  params?: {
    email: string;
    password: string;
    referral_id: number;
    channelID?: string;
    additional?: string;
    ftu_popup?: boolean;
    ftu_url?: string;
    ftu?: string;
    ftu_flags?: number;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/email', params, config);
};

/*
 * 微信登录
 * @param params
 * @param config
 */
export const loginWithWechat = (
  params?: {
    code: string;
    referral_id: number;
    referrer_id?: string;
    ep_partner?: string;
    additional?: string;
    wx_api_type?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/wechat', params, config);
};

/**
 * 邮箱注册
 * @param params
 * @param config
 */
export const emailRegister = (
  params: { email: string; password: string; referral_id: number } & ObjectType,
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/signup', params, config);
};

/**
 * 学生入口邮箱注册
 * @param params
 * @param config
 */
export const emailRegisterByStudent = (
  params: {
    email: string;
    password: string;
    referral_id?: number;
    referrer_id?: string;
    recaptcha_response?: string;
    timestamp?: number;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/signup/student', params, config);
};

export const sendStdEmailVerify = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/email/user_student_url/send', {}, config);
};

export const sendStdEmailVerifyWithParams = (
  params?: { email?: string; source?: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/user_student_url/send', params, config);
};

export const sendStdVerifyCode = (
  params: {
    key?: string;
    source?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/user_student_url', params, config);
};

export const getSchoolInfo = (channel: string, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`ec/customer/event/student/${channel}/info`, {}, config);
};
export const getSchoolShare = (
  params: { channel: string; referral_id: number },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.get(
    `ec/growth/invite/student/campaign/${params.channel}/share/info`,
    { referral_id: params.referral_id },
    config
  );
};

/**
 * 重新发送邮箱验证码
 * @param config
 */
export const emailResend = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/email/verify_url/send', {}, config);
};

/**
 * 验证邮箱结果
 * @param config
 */
export const emailVerifyResult = (
  params: { user_id: string; reset_key: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/verify_url', params, config);
};

/**
 * facebook登录
 * @param access_token Facebook user accessToken（防欺诈服务端校验用）
 * @param user_info  The facebook user information string with JSON format: "id, name, first_name, last_name, picture.type(large), email"
 */
export const facebookLogin = (
  params: {
    user_info: any;
    /** Facebook user accessToken；服务端用 Graph API 校验身份 */
    access_token?: string;
    referral_id?: number;
    ftu_popup?: boolean;
    ftu_url?: string;
    ftu?: string;
    ftu_flags?: number;
    referrer_id?: string;
    page_url?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/facebook', params, config);
};

/**
 * google登录
 * @param refresh_token
 * @param user_info  The facebook user information string with JSON format: "id, name, first_name, last_name, picture.type(large), email"
 */
export const googleLogin = (
  params: {
    user_info: string;
    referral_id: number;
    ftu_popup?: boolean;
    ftu_url?: string;
    ftu?: string;
    ftu_flags?: number;
    id_token?: string;
    // 新增channel、page_url，用于记录google一键登录用户数据
    channel?: string;
    page_url?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/google', params, config);
};
/**
 * 退出登录
 */
export const logout = (): Promise<AResponse> => {
  return fetch.get('/ec/customer/logout', {});
};

/**
 * 获取profile页面信息
 * @param params
 * @param config
 */
export const getProfile = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/account/profile`, {}, config);
};

/**
 * 修改profile页面信息
 * @param params
 * @param config
 */
export const editProfile = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(`/ec/customer/account/profile`, params, config);
};

/**
 *
 * @param language 设置语言
 * @param config
 */
export const setLanguage = (language, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/language/update/${language}`, {}, config);
};

/* 获取问卷信息
 * @param data 请求参数
 * @returns 返回值
 */
export const getSurveyInfo = (
  data: {
    user_id?: string;
    group: 'a' | 'b' | string;
    survey_id?: string;
    ref_type?: string;
    ref_id?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.get('/ec/customer/survey/info', data, config);
};

/**
 * 提交答案
 * @param data 参数
 * @returns 返回数据
 */
export const setSurveyAnswer = (data: {
  task_id: string | number;
  user_id?: string;
  status: 'F' | 'P' | 'Q' | 'E'; //用户完成答卷状态 F：用户点击send to weee；P：用户完成了NPS部分；Q：没有回答任意问题;E:回答部分附加题 没有提交
  ref_type: 'account_page' | 'order_confirm' | 'home_page' | 'order_list' | 'order_detail'; //用户在什么页面回答的(me order_confirm)
  answer?: {
    [key: string]: {
      rating?: string;
      option_ids?: string;
      comment?: string;
    };
  };
}): Promise<AResponse> => {
  return fetch.post('/ec/customer/survey/answer', data);
};
/*
 * Login Connects
 * @param config
 */
export const loginConnects = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/login/connects', {}, config);
};

/**
 * 通过zipcode查询是否满足recipo服务区域
 * @param params string
 * @param config
 */

export const getRicepoSupport = (params?: { zipcode: string }, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/me/support_ricepo', params, config);
};

/*
 * 保存地址
 * @param params
 * @param config
 */

export const saveAddress = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/addresses/save', params, config);
};

/*
 * 删除地址
 * @param params
 * @param config
 */
export const deleteAddress = (params: { address_id: string }, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/addresses/delete', params, config);
};

export const getAdminAuth = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/check_home_us', params, config);
};

/**
 * 激活免费会员
 **/
export const activeTrialMember = (
  params?: {
    keyword: string | number;
    type: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post(`/ec/customer/member/submit_free_trial`, params, config);
};

export const getProgramPerks = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  const timezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  return fetch.get(
    '/ec/customer/member/perks/new',
    {},
    {
      ...config,
      headers: { ...config?.headers, [Constants.TIME_ZONE]: timezone }
    }
  );
};

// 会员入口页面接口
export const getVipInfo = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/member/protal', {}, config);
};

// 试用会员页面接口
export const getVipFreeTrial = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/member/trial_landing', {}, config);
};

// 购买会员列表
export const getVipPurchase = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/member/loading', {}, config);
};

// 会员订单成功页面
export const getVipOrderInfo = (orderId: string, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/member/order_success/${orderId}`, {}, config);
};

/**
 * 试用会员:
 * @param config
 */
export const setVipTrial2 = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/member/free_trail', {}, config);
};

/**
 * 积分
 **/
// get points info on landing page
export const getPointsInfo = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/points_new/info', {}, config);
};

// get points detail history
export const getPointsDetail = (
  params?: { type: string; pageSize: number; page: number },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/points_new/transaction_list', params, config);
};

// activate inactive points
export const activatePoints = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/points_new/active', {}, config);
};

// get points group buy list
export const getPointsGroupBuyList = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/points/list_points_group_buy', {}, config);
};

/**
 * 积分礼品卡
 **/
// check giftcard balance
export const checkGiftCard = (code: any, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/points/redeem_points_check', { code }, config);
};

// redeem giftcard
export const redeemGiftCard = (code: any, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/points/redeem_points_by_code', { code }, config);
};

export const setRemindEmail = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(`ec/customer/email/subscribe`, params, config);
};

export const getAddressInfo = (addressId: number, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/so/address/info/${addressId}`, config);
};

/**
 * 检查问卷是否呈现引导
 * @param params
 * @param config
 * @returns
 */
export const getOrderRatingGuide = (
  params?: { survey_id: number },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.get('/ec/customer/survery/survey_guide/info', params, config);
};

/**
 * 修改问卷引导
 * @param params
 * @param config
 * @returns
 */
export const updateOrderRatingGuide = (
  params?: { survey_id: number; status: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/survery/survey_guide/update', params, config);
};

/**
 *  points 团购页面
 * @param config
 * @returns
 */

export const getPointsProductList = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/points/list_points_product', {}, config);
};

/**
 *  points 团购被邀请人list页面
 * @param config
 * @returns
 */

export const getPointsLandingProductList = (orderId, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/points/list_points_product_for_referrer?order_id=${orderId}`, {}, config);
};

/**
 * member_share_list
 * 会员跟买进度list
 */
export const getMemberShareList = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/member/share_list`, {}, config);
};

/** 获取邮件中URL的重定向信息 */
export const getCollectionRedirectInfo = (params: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/collection/redirect_info', params, config);
};

export const getStudentDiscountPerks = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/event/student/info`, {}, config);
};
export const getStudentSignupBenefits = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/event/signup/student`, {}, config);
};

/**
 * 获取个性化推荐状态
 * @param params
 * @param config
 * @returns
 */
export const getPersonalSetting = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/user/personal_setting`, {}, config);
};

/**
 * 校验first time use的邮箱
 * @param params
 * @param config
 * @returns
 */
export const checkFirstTimeUseEmail = (params: any, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(`/ec/customer/event/show_message`, params, config);
};

/**
 * 选择first time use的store
 * @param params
 * @param config
 * @returns
 */
export const chooseFirstTimeUseStore = (params: any, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(`/ec/customer/event/choose_store`, params, config);
};

/**
 * 无密码注册
 * @param params
 * @param config
 * @returns
 */
export const signUpWithoutPassword = (params: any, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(`/ec/customer/signup/email/web/v1`, params, config);
};

/**
 * 发设置密码邮件
 * @param params
 * @param config
 * @returns
 */
export const sendSetPasswordEmail = (params: any, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(`/ec/customer/email/first_time_email`, params, config);
};

/**
 * 无密码登录
 * @param params
 * @param config
 * @returns
 */
export const loginWithoutPassword = (params: any, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(`/ec/customer/event/set_first_time`, params, config);
};

/**
 * 更新个性化推荐状态
 * @param params
 * @param config
 * @returns
 */
export const updatePersonalSetting = (params: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(`/ec/customer/user/personal_setting`, params, config);
};

/**
 * 获取Settings页面信息
 * @param config
 */
export const getSettingsData = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  const appChannel = getCookie(Constants.APP_CHANNEL, config?.ctx);
  return fetch.get('/ec/customer/user/me/profile', params, {
    headers: { ...config?.headers, [Constants.APP_CHANNEL]: appChannel }
  });
};

/**
 * 获取Settings通知页面开关信息
 * @param config
 */
export const getSettingNotificationData = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/account/notification', params, config);
};

/**
 * 修改Settings通知页面开关信息
 * @param config
 */
export const changeSettingNotificationData = (
  params?: ObjectType,
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/account/notification', params, config);
};

/**
 * 获取member页面信息
 * @param config
 */
export const getSettingMemberData = (params?: ObjectType): Promise<AResponse> => {
  return fetch.get(`/ec/customer/member/${params.userId}/info`);
};

/**
 * Settings页面修改密码
 * @param config
 */
export const changeSettingsPassword = (params?: ObjectType): Promise<AResponse> => {
  return fetch.post(`/ec/customer/password/resetWithMe`, params);
};

/**
 * 无密码注册用户设置密码
 * @param config
 */
export const setPassword = (params?: ObjectType): Promise<AResponse> => {
  return fetch.post(`/ec/customer/password/resetWithEmail/v1`, params);
};

/**
 * 传递Settings头像上传后的图片地址
 * @param config
 */
export const getSettingsPicUpdateUrl = (url: string): Promise<AResponse> => {
  return fetch.get(`/ec/customer/user/user_image/update?imageUrl=${url}`);
};

/**
 * 发送绑定邮箱的电子邮件
 * @param config
 */
export const getSendEmail = (): Promise<AResponse> => {
  return fetch.get(`/ec/customer/email/verify_url/send`);
};

/** 删除账号操作日志接口 */
export const accountDeletionStep = (
  data: {
    causeId?: number | null;
    causeOther?: string | null;
    type?: 'popup_confirm ' | 'question_confirm ' | 'success_loading';
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/account/account_deletion_step', data, config);
};
/** 删除账号landing页面接 */
export const accountDeletionLoading = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/account/account_deletion_loading', {}, config);
};
/** 删除账号操作接口 */
export const accountDeletion = (
  params: { userId: number; confirmPassword?: string; verify_code?: string },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/account/account_deletion', params, config);
};

/**
 * 试用会员:
 * @param config
 */
export const setVipTrial = (params: { keyword: string }, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/vip/free_trial', params, config);
};

/*
 * Account-faq列表
 * @param config
 */
export const getFaqList = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/faq`, {}, config);
};

/**
 * sms notification
 * @param config
 */
export const getSmsNotice = (params: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/account/sms_notification`, params, config);
};

/**
 * sms notification更新
 */
export const updateSmsNotice = (params: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/account/sms_notification', params, config);
};

/**
 * rewards level
 */
export const loyaltyLevel = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/account/rewards/level', {}, config);
};

/**
 * rewards landing
 */
export const loyaltyLanding = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/account/rewards/landing', {}, config);
};

/*
 * 消息中心大模块接口
 */
export const getNotificationListData = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/message/category', {}, config);
};

/**
 * 消息中心小模块接口
 */
export const getNotificationSubListData = (
  params: {
    category?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.get(`/ec/customer/message/sub_category?category=${params.category}`, {}, config);
};

/**
 * 消息中心小模块消息列表接口
 */
export const getNotificationSubMessageList = (
  params: {
    category_id?: string;
    limit?: number;
    start_id: string;
    unread?: boolean;
    create_timestamp?: number;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.get(`/ec/customer/message/list`, params, config);
};

/**
 * 清除未读模块
 */
export const clearAllUnreadModule = (
  params: {
    category_id?: string;
    id?: string;
    category?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/message/read', params, config);
};

/**
 * 清除单条消息
 */
export const clearSingleUnreadMessage = (
  params: {
    id: number;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/message/read', params, config);
};

/**
 * 删除未读消息
 */
export const deleteNotificationMessage = (
  params: {
    category_id?: string;
    ids?: number[];
    category?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/message/clear', params, config);
};

/**
 * 获取email 验证结果
 */
export const getEmailVerification = (
  params: {
    user_id?: string;
    reset_key?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/user_verify_url', params, config);
};

/**
 * 发送 verification email
 */
export const sendEmailVerification = (
  params: {
    code?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/user_verify_url/send', params, config);
};

/**
 * 登录流程发送 verification email v1
 */
export const sendEmailVerificationV1 = (
  params: {
    key?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/user_verify_url/v1/send', params, config);
};

/**
 * 注册流程发送 verification email v2
 */
export const signSendEmailVerification = (
  params: {
    key?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/user_verify_url/v2/send', params, config);
};

/**
 * 获取验证页面内容信息
 */
export const getVerifyPageMessage = (
  params: {
    key?: string;
    email?: string;
    source?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/user_verify_url/v1/info', params, config);
};

/**
 * 发送验证信息code校验
 */
export const sendVerifyCode = (
  params: {
    code?: string;
    key?: string;
    referral_id?: number;
    channelID?: string;
    ep_partner?: string;
    referrer_id?: string;
  } & ObjectType,
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/login/email/verify_code', params, config);
};

/**
 * 注册流程code校验
 */
export const signSendVerifyCode = (
  params: {
    code?: string;
    key?: string;
    referral_id?: number;
    channelID?: string;
    ep_partner?: string;
    referrer_id?: string;
    page_url?: string;
  } & ObjectType,
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/signup/email/code/v1', params, config);
};

/**
 * Magic-Sign-in
 * 校验用户邮箱登录受否有密码
 * @param params
 * @param config
 */
export const checkEmail = (params: { value: string }, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post(`/ec/customer/event/check_first_time`, params, config);
};

/**
 * 用户登录, 通过邮件链接跳转做邮箱验证
 * @param params
 * @param config
 * @returns
 */
export const verifyEmailLink = (params: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/verify_url/v1', params, config);
};

/**
 * points 福利购买页面
 * @param config
 * @returns
 */
export const getPointsProductListActivity = (
  params?: ObjectType,
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.get('/ec/customer/points/list_points_product/activity', params, config);
};

/**
 * 获取rewards wednesday活动信息
 * @param params
 * @param config
 * @returns
 */
export const getRewardsPointsUpgrade = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/account/rewards/point_upgrade`, params, config);
};

/**
 * 获取rewards wednesday活动信息
 * @param params
 * @param config
 * @returns
 */
export const getRewardsUpgraded = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/customer/account/rewards/point_upgrade_success`, params, config);
};

/**
 * 获取手机验证页面内容信息
 */
export const getPhoneVerifyPageMessage = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/phone/user_verify_url/v1/info', params, config);
};

/**
 * User Free Gift
 * @param params
 * @param config
 * @returns
 */
export const getUserFreeGiftInfo = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get(`/ec/promotion/promotions/popup/content`, params, config);
};

/**取消订单判断积分返回判断
 */
export const cancelRequestCache = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/so/order/cache/cancel_request', params, config);
};

/**获取取消订单时存储的信息
 */
export const getCancelOrderCacheBody = (
  uuid?: string,
  params?: ObjectType,
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.get(`/ec/so/order/query/cancel/request/${uuid}`, params, config);
};

/**判断是否可以进入积分退货页面
 */
export const checkPointsRefund = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/so/config/refund_points_flag/value', {}, config);
};

/**售后时判断是否可以进入积分退货页面
 */
export const checkPointsRefundAfterSale = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/so/config/return_refund_points_flag/value ', {}, config);
};

/**
 * 获取rewards landing banner信息
 * @param params
 * @param config
 * @returns
 */
export const getRewardsLandingBanner = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/account/rewards/landing_info', params, config);
};

/**
 * 邮费和服务费活动
 *
 */
export const getLowerMinimumPopup = (
  params?: {
    type: number; // 1: 低门槛 2: 无门槛
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/account/rewards/activity/index_popup', params, config);
};

/**
 * 手机号和邮箱验证
 *
 */
export const getSecurityConfirmInfo = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/user/security/account/confirm/info', params, config);
};
/**
 * 手机号和邮箱确认
 *
 */
export const confirmSecurityConfirmInfo = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/user/security/account/confirm', params, config);
};

/**
 * 运费活动 landing 分享
 * @param params
 * @param config
 * @returns
 */
export const fetchLowerMinimumShare = (
  params?: {
    type: number | null;
    school_key?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post(`/ec/customer/account/rewards/activity/landing_page_share`, params, config);
};

/**
 * 运费活动 landing 页面数据
 * @param params
 * @param config
 * @returns
 */
export const fetchLowerMinimum = (
  params?: {
    type: number;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post(`/ec/customer/account/rewards/activity/landing_page`, params, config);
};

/**
 * update customer email
 */
export const updateCustomerEmail = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.post('/ec/customer/user/address/update_email', params, config);
};

export const getCartRewardsShipping = (
  params: {
    type: number;
    diff_amount: number;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.get('/ec/customer/account/rewards/cart_shipping_popup', params, config);
};

export const closeAccountLoadPoints = (
  params: {
    level: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/account/loyalty/close/points/plan', params, config);
};

/**
 * 检测邮箱是否需要recaptcha v2验证
 */
export const isEmailNeedRecaptchaV2 = (params?: ObjectType, config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/event/check_google_recaptcha_v2', params, config);
};

export const getCartTopMessage = (
  params: {
    version: string;
    freeTrialGold: number;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.get('/ec/customer/messages/home_header', params, config);
};

/**
 * 获取账号运费、服务费数据
 */
export const getGoldRewardsInfo = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/account/rewards/index_shipping_popup', {}, config);
};

/**
 * 升级账户至Gold级别
 */
export const upgradeToGold = () => {
  return fetch.get('/ec/customer/account/rewards/claim_trial_gold');
};
/**
 * 获取临时黄金账号过期日期
 */
export const getAccountGoldRewardsExpiryDate = (): Promise<AResponse> => {
  return fetch.get('/ec/customer/account/rewards/get_gold_trial_end_day');
};

export type UpgradeInfo = {
  title?: string;
  is_student?: boolean;
  school_info?: any;
  benefits: {
    desc: string;
    icon_url: string;
    tag: null | string;
    title: string | null;
  }[];
};
/**
 * 获取黄金账号升级信息
 *
 */
export const getGoldRewardsUpgradeInfo = (): Promise<AResponse> => {
  return fetch.get<UpgradeInfo>('/ec/customer/account/rewards/index_gold_trial_popup');
};

/**
 * get update email page message
 *
 */
export const getUpdateEmailPageMessage = (
  params: {
    email: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/verify_code/v2/info', params, config);
};

/**
 * update email send code
 *
 */

export const updateEmailSendCode = (
  params?: {
    email?: string;
    source?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/verify_code/send/v2', params, config);
};

/**
 * update email send code verify
 *
 */

export const updateEmailVerifyCode = (
  params: {
    email: string;
    verify_code: string;
    source?: string;
  },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/email/verify_code/v2', params, config);
};

/**
 * 通用基础查询
 */
export const queryCustomerCommBase = (
  params: { options: string[] },
  config?: NewAxiosRequestConfig
): Promise<AResponse> => {
  return fetch.post('/ec/customer/comm/base/query', params, config);
};

/**
 * @param config
 * 获取当前网站支持的所有语言
 */
export const getSupportLanguages = (config?: NewAxiosRequestConfig): Promise<AResponse> => {
  return fetch.get('/ec/customer/language/support_language/v1', {}, config);
};
